import { useNavigate } from 'react-router-dom';

import { emailGoodFaithEstimate, textGoodFaithEstimate } from 'api/Appointment';

import { cn, iaRa, mString } from 'lib/helpers/utility';
import { addYears, formatDate } from 'lib/helpers/dateUtils';
import { useDocumentShare } from 'lib/hooks/useDocumentShare';
import { downloadGoodFaithEstimatePdf } from 'lib/helpers/pdf/download/download';

import Modal from 'components/shared/Modal/Modal';
import DocText from 'components/shared/DocumentSheet/DocText';
import DocumentPages from 'components/shared/DocumentSheet/DocumentPages';
import DocFooter from 'components/shared/DocumentSheet/components/DocFooter';
import DocHeader from 'components/shared/DocumentSheet/components/DocHeader';
import { DATE_MEDIUM } from 'components/shared/DocumentSheet/lib/docFormats';
import { buildDocLegal } from 'components/shared/DocumentSheet/lib/docLegal';
import DocFieldGrid from 'components/shared/DocumentSheet/components/DocFieldGrid';
import { DOC_GAPS, DOC_TEXT } from 'components/shared/DocumentSheet/lib/docStyles';
import { getPracticeName } from 'components/shared/DocumentSheet/lib/practiceLines';
import DocSectionHead from 'components/shared/DocumentSheet/components/DocSectionHead';
import DocCompactHeader from 'components/shared/DocumentSheet/components/DocCompactHeader';
import DocumentZoomToggle from 'components/shared/DocumentSheet/components/DocumentZoomToggle';
import { DocumentZoomProvider } from 'components/shared/DocumentSheet/lib/documentZoomContext';
import DocumentShareActions from 'components/shared/DocumentSheet/components/DocumentShareActions';

import { buildGfeItemsBlock, GfeResponsibility } from './components/GfeItems';
import GfeDisclosures, { GfeAcknowledgment } from './components/RequiredDisclosures';

const round = (n) => Math.round(Number(n) || 0);

const CheckinGoodFaithEstimate = ({
  isOpen,
  onClose,
  practice,
  patient,
  procedures,
  appointmentId,
  channels
}) => {
  const navigate = useNavigate();

  const { busy, handleDownload, handleSend } = useDocumentShare({
    noun: 'estimate',
    sendParams: { appointmentId },
    downloadRequest: () => downloadGoodFaithEstimatePdf({ navigate, appointmentId }),
    emailRequest: emailGoodFaithEstimate,
    textRequest: textGoodFaithEstimate
  });

  const timezone = practice?.timezone;
  const issuedOn = formatDate(new Date(), timezone, DATE_MEDIUM);
  const validThrough = formatDate(addYears(new Date(), 1), timezone, DATE_MEDIUM);

  const visibleProcedures = iaRa(procedures);
  const totalCharges = visibleProcedures.reduce(
    (sum, procedure) => sum + round(procedure?.charge),
    0
  );

  const primaryProcedure = visibleProcedures[0];
  const primaryService = primaryProcedure
    ? [
        primaryProcedure?.name || 'Service',
        primaryProcedure?.procedure_code ? `(${primaryProcedure.procedure_code})` : ''
      ]
        .filter(Boolean)
        .join(' ')
    : '';

  const practiceName = getPracticeName(practice);
  const patientName = [patient?.f_name, patient?.l_name].filter(Boolean).join(' ');

  const preparedFor = [
    { label: 'Patient name', value: patientName },
    { label: 'Date of birth', value: patient?.dob ? formatDate(patient.dob) : '' },
    { label: 'Patient chart #', value: patient?.id ? `PC-${patient.id}` : '' },
    { label: 'Primary item / service', value: primaryService },
    {
      label: 'Items estimated',
      value: visibleProcedures.length ? `${visibleProcedures.length}` : ''
    },
    { label: 'Total estimated cost', value: mString(totalCharges) }
  ];

  const itemsNote = [
    practiceName,
    practice?.npi ? `NPI ${practice.npi}` : '',
    practice?.tax_id ? `Tax ID: ${practice.tax_id}` : ''
  ]
    .filter(Boolean)
    .join(' · ');

  const legal = buildDocLegal(
    practice,
    'No Surprises Act (45 C.F.R. §149.610) – this estimate is not a bill.'
  );

  const blocks = [
    {
      key: 'prepared-for',
      node: (
        <div className={DOC_GAPS.section}>
          <DocSectionHead title="Prepared for" />
          <DocFieldGrid entries={preparedFor} />
        </div>
      )
    },
    {
      key: 'lede',
      node: (
        <DocText size="xs" className={cn(DOC_TEXT.bodyMuted, DOC_GAPS.section)}>
          This is a Good Faith Estimate of expected charges for the items and services listed below,
          furnished under the No Surprises Act. This is only an estimate –{' '}
          <span className="font-700 text-neutral-900">it is not a bill</span> – and your actual
          items, services, and charges may differ.
        </DocText>
      )
    },
    buildGfeItemsBlock({
      procedures: visibleProcedures,
      total: totalCharges,
      note: itemsNote
    }),
    { key: 'responsibility', node: <GfeResponsibility total={totalCharges} /> },
    { key: 'disclosures', node: <GfeDisclosures /> },
    { key: 'acknowledgment', node: <GfeAcknowledgment practice={practice} /> }
  ].filter(Boolean);

  return (
    <DocumentZoomProvider>
      <Modal
        isOpen={isOpen}
        handleClose={onClose}
        title="Good Faith Estimate"
        width="large"
        headButton={
          <div className="flex w-full items-center justify-end gap-2 pr-3">
            <DocumentZoomToggle />
            <DocumentShareActions
              busy={busy}
              onDownload={handleDownload}
              onSend={handleSend}
              canSend={!!appointmentId}
              channels={channels}
            />
          </div>
        }
        bodyClassName="bg-neutral-100 p-6 xs:p-3"
        overlayClassName="xs:!p-0"
        className="xs:!rounded-none"
        isFooter={false}>
        <div className="flex justify-center">
          <DocumentPages
            blocks={blocks}
            renderHeader={(pageIndex) =>
              pageIndex === 0 ? (
                <DocHeader
                  practice={practice}
                  title="Good Faith Estimate"
                  kicker="Patient estimate – not a bill"
                  lines={[
                    [
                      appointmentId ? { label: 'GFE ', value: `#${appointmentId}` } : null,
                      { label: 'Issued ', value: issuedOn }
                    ].filter(Boolean),
                    [{ label: 'Valid through ', value: validThrough }]
                  ]}
                />
              ) : (
                <DocCompactHeader
                  practice={practice}
                  reference={
                    appointmentId ? `Good Faith Estimate #${appointmentId}` : 'Good Faith Estimate'
                  }
                  meta={[issuedOn, patientName].filter(Boolean).join(' · ')}
                />
              )
            }
            renderFooter={({ page, pageCount }) => (
              <DocFooter legal={legal} page={`Page ${page} of ${pageCount}`} />
            )}
          />
        </div>
      </Modal>
    </DocumentZoomProvider>
  );
};

export default CheckinGoodFaithEstimate;
